/**
 * POS-FULFILLMENT MODULE
 * Self-contained. Import only from /shared or within this module.
 * Do not import from other modules directly.
 *
 * approval :: the review gate on embed-sourced sample requests.
 *
 * Migration 0097 replaced item 28's "the submitter has no field that influences
 * where the goods ship" invariant with a human review step, because a sample
 * request carries a FREE-TEXT ship-to typed by an anonymous caller. The database
 * is the enforcer:
 *
 *   • `source = 'embed'` rows are rewritten to `approval_status = 'pending'` on
 *     insert by a BEFORE INSERT trigger — the public endpoint cannot self-approve.
 *   • a BEFORE UPDATE trigger RAISES `check_violation` if a row whose
 *     approval_status is 'pending' or 'rejected' is moved to `status = 'Shipped'`.
 *
 * This file holds the pure, testable half of that rule so the screen, the
 * shipping modal and the service all read the SAME predicate rather than three
 * hand-rolled string comparisons. Nothing here is a security control — the
 * trigger is. These functions exist so a reviewer never *reaches* the trigger's
 * refusal by accident and gets a sentence instead of "Could not save".
 *
 * Row shape: accepts either the camelCase envelope `normalizeSampleRequest`
 * produces (`approvalStatus`, `source`) or a raw snake_case DB row
 * (`approval_status`), so it can be used on both sides of the service.
 */

export const APPROVAL = {
  NOT_REQUIRED: "not_required",
  PENDING: "pending",
  APPROVED: "approved",
  REJECTED: "rejected",
};

/** The only two values the service will ever write. Not caller-suppliable. */
export const APPROVAL_DECISIONS = Object.freeze([APPROVAL.APPROVED, APPROVAL.REJECTED]);

/** Every value the 0097 CHECK constraint allows. */
const ALL_APPROVAL_STATUSES = new Set(Object.values(APPROVAL));

/** The two the trigger refuses to ship. */
const SHIP_BLOCKING = new Set([APPROVAL.PENDING, APPROVAL.REJECTED]);

/** Read the approval status off either row shape, defaulting the way the DB does. */
export function approvalStatusOf(row) {
  const v = row?.approvalStatus ?? row?.approval_status ?? APPROVAL.NOT_REQUIRED;
  // An unrecognised value can only mean the schema moved ahead of this code.
  // Treat it as "needs a human" rather than "fine to ship" — fail closed.
  return ALL_APPROVAL_STATUSES.has(v) ? v : APPROVAL.PENDING;
}

/** Did this row come through the public sample-request form? */
export function isFromEmbed(row) {
  return (row?.source ?? "app") === "embed";
}

/** Waiting on a human. The count behind "N requests are waiting on you". */
export function isAwaitingApproval(row) {
  return approvalStatusOf(row) === APPROVAL.PENDING;
}

/** True when the DB trigger would refuse to let this row reach 'Shipped'. */
export function isShipBlocked(row) {
  return SHIP_BLOCKING.has(approvalStatusOf(row));
}

export const SHIP_BLOCKED_PENDING =
  "This request came in through the public sample form, so it can't ship until someone approves it. " +
  "Open Review approval on the row, check the ship-to address, then Approve or Reject.";

export const SHIP_BLOCKED_REJECTED =
  "This request was rejected, so it can't be marked Shipped.";

/** The sentence to show instead of letting the trigger answer. Null when shippable. */
export function shipBlockedMessage(row) {
  const s = approvalStatusOf(row);
  if (s === APPROVAL.PENDING) return SHIP_BLOCKED_PENDING;
  if (s === APPROVAL.REJECTED) return SHIP_BLOCKED_REJECTED;
  return null;
}

/**
 * The fulfilment statuses this row may actually be moved to. 'Shipped' is
 * REMOVED (not merely disabled) for a blocked row, so the control that would
 * hit the trigger is not on screen at all.
 */
export function shipStatusOptions(allStatuses, row) {
  if (!isShipBlocked(row)) return allStatuses;
  return allStatuses.filter((s) => s !== "Shipped");
}

/**
 * The review badge that sits beside the fulfilment status, or null when the row
 * is an ordinary rep-entered request (`not_required`) — those must look exactly
 * as they do today.
 */
export function approvalBadge(row) {
  switch (approvalStatusOf(row)) {
    case APPROVAL.PENDING:  return { label: "Needs approval", cls: "sp-appr-pending" };
    case APPROVAL.APPROVED: return { label: "Approved",       cls: "sp-appr-approved" };
    case APPROVAL.REJECTED: return { label: "Rejected",       cls: "sp-appr-rejected" };
    default: return null;
  }
}

/** Reject reasons are stored; cap and trim before they reach the column. */
export const REJECT_REASON_MAX = 1000;

export function normalizeRejectReason(reason) {
  return String(reason ?? "").trim().slice(0, REJECT_REASON_MAX);
}
